import { getPointCloudHiddenSourcesProperty, hasGlobalPropertyService } from '@pointorama/potree-shared';

type SourceColor = [number, number, number, number];

export interface SourceInfoById {
  [key: string]: { color: SourceColor; visible: boolean };
}

type Source = {
  id: number;
  color: SourceColor;
  visible: boolean;
  pointCloudId: string;
};

export class SourcesInfo {
  sources: Source[] = [];

  getSourceIdsForPointCloud({ pointcloudId }: { pointcloudId: string }) {
    return this.sources.filter((source) => source.pointCloudId === pointcloudId);
  }

  getSortedSourceIdsForPointCloud(pointcloudId: string) {
    const sourceIdsForPointcloud = this.getSourceIdsForPointCloud({ pointcloudId });
    return sourceIdsForPointcloud.reduce((sortedSourceIds: SourceInfoById, sourceIdItem) => {
      const { id: key, ...value } = sourceIdItem;
      sortedSourceIds[key] = value;
      return sortedSourceIds;
    }, {});
  }

  addSources({ sources, pointCloudId }: { sources: number[]; pointCloudId: string }) {
    const generateColor = this.getColorGenerator(pointCloudId);
    // Honour the reactive `pointcloud_hidden_sources` property at creation time so visibility
    // is correct regardless of the order in which addSources runs vs the PointCloudOctree
    // subscription firing (e.g. before/after property service preload).
    const hiddenSet = new Set<number>(this.readHiddenSources(pointCloudId));
    for (const id of sources) {
      const color: SourceColor = generateColor();
      const newSource: Source = {
        id,
        color,
        visible: !hiddenSet.has(id),
        pointCloudId,
      };
      this.sources.push(newSource);
    }
  }

  private readHiddenSources(pointCloudId: string): number[] {
    if (!hasGlobalPropertyService()) return [];
    const property = getPointCloudHiddenSourcesProperty(pointCloudId);
    return Array.isArray(property.value) ? (property.value as number[]) : [];
  }

  getColorGenerator(str: string): () => SourceColor {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = (h * 31 + str.charCodeAt(i)) >>> 0;
    }

    return function () {
      h = (h * 1664525 + 1013904223) >>> 0;
      const r = h / 2 ** 32;

      h = (h * 1664525 + 1013904223) >>> 0;
      const g = h / 2 ** 32;

      h = (h * 1664525 + 1013904223) >>> 0;
      const b = h / 2 ** 32;

      return [r, g, b, 1];
    };
  }
}
